import { extractUuidFromUrl } from '@/components/forms/Attachments/utils';
import * as tus from 'tus-js-client';
import { throttle } from 'lodash';

interface FileUploadOptions {
  blobContainerName: 'Files' | 'Pictures';
  endpoint: string;
}

export type UploadProgress =
  // Initial state upload ist starting shortly
  | { state: 'idle' }
  // The file upload is currently running
  | {
      state: 'pending';
      bytesUploaded: number;
      bytesTotal: number;
      percentage: number;
      abort: tus.Upload['abort'];
    }
  // File is successfully uploaded
  | { state: 'success' }
  // The file upload failed
  | { state: 'failed' };

export const defaultFileUploadOptions: FileUploadOptions = {
  blobContainerName: 'Files',
  endpoint: '/api/upload-files',
};

interface UploadFileProps {
  file: File;
  name: string;
  onUpdate?: (progress: UploadProgress) => void;
  onReceivedId?: (id: string) => void;
  onError?: (error: Error | tus.DetailedError) => void;
  onSuccess?: () => void;
}

interface FileUploadProps extends UploadFileProps {
  options: FileUploadOptions;
}

export function useFileUpload(options: FileUploadOptions = defaultFileUploadOptions) {
  async function uploadFile({
    file,
    name,
    onUpdate,
    onReceivedId,
    onError,
    onSuccess,
  }: UploadFileProps) {
    return upload({ file, name, options, onUpdate, onError, onReceivedId, onSuccess });
  }

  return {
    uploadFile,
  };
}

function calculateChunkSize(fileSize: number): number {
  const ALIGNMENT = 256 * 1024; // 256 KB is the tus.io alignment requirement
  const MAX_CHUNK_SIZE = 100 * 1024 * 1024; // 100 MB

  let chunkSize = fileSize / 10;

  if (chunkSize > MAX_CHUNK_SIZE) return MAX_CHUNK_SIZE;

  chunkSize = Math.ceil(chunkSize / ALIGNMENT) * ALIGNMENT;

  // prevent to have chunk size greater then file size
  chunkSize = Math.min(chunkSize, fileSize);
  return chunkSize;
}

async function upload({
  file,
  name,
  options,
  onUpdate,
  onSuccess,
  onError,
  onReceivedId,
}: FileUploadProps) {
  let id: string;

  // This wait-time needs to be larger than the debounceWait value for validation (in createSchemaValidator)
  const throttledUpdate = onUpdate
    ? throttle(onUpdate, 300, { leading: true, trailing: false })
    : undefined;

  if (file.size === 0) {
    onUpdate?.({ state: 'failed' });
    onError?.({ name: 'EmptyFile', message: 'File is empty!' });
    return;
  }
  /**
   * If the file type is not set, we use application/octet-stream
   *
   * NOTE: 'application/octet-stream' is a fallback for files that are not supported by the browser.
   * Otherwise the browser will set file.type to empty string which is sent to the server
   * and the server rejects it
   */
  const resolvedContentType = file.type || 'application/octet-stream';
  const tusUpload = new tus.Upload(file, {
    // Endpoint is the upload creation URL from your tus server
    endpoint: options.endpoint,
    // Retry delays will enable tus-js-client to automatically retry on errors
    retryDelays: null, //[0, 3000, 5000, 10000, 20000],
    storeFingerprintForResuming: false,
    // Attach additional meta data about the file for the server
    metadata: {
      name,
      contentType: resolvedContentType,
      fileSize: String(file.size),
      blobContainerName: options.blobContainerName,
    },
    // Callback for errors which cannot be fixed using retries
    onError(error) {
      onUpdate?.({ state: 'failed' });
      onError?.(error);
    },
    chunkSize: calculateChunkSize(file.size),
    // Callback for reporting upload progress
    onProgress(bytesUploaded, bytesTotal) {
      throttledUpdate?.({
        bytesUploaded,
        bytesTotal,
        percentage: (bytesUploaded / bytesTotal) * 100,
        state: 'pending',
        abort: tusUpload.abort.bind(tusUpload),
      });
    },
    onAfterResponse(req) {
      // PATCHES have fileID in the url
      if (req.getMethod() === 'PATCH') {
        const idFromUrl = extractUuidFromUrl(req.getURL());
        if (idFromUrl && !id) {
          id = idFromUrl;
          onReceivedId?.(idFromUrl);
        }
      }
    },
    // Callback for once the upload is completed
    onSuccess() {
      onUpdate?.({
        state: 'success',
      });
      onSuccess?.();
    },
  });

  // Check if there are any previous uploads to continue.
  const previousUploads = await tusUpload.findPreviousUploads();
  // Found previous uploads so we select the first one.
  if (previousUploads.length) {
    tusUpload.resumeFromPreviousUpload(previousUploads[0]);
  }

  // Start the upload
  tusUpload.start();
}
